import { get, writable, type Writable } from "svelte/store";
import type { RuntimeState, ChatMessage, ChatSession } from "./types";
import type { SseEvent, SseToolCallEvent, SseUsageEvent } from "excel-agent-core";
import {
    sendMessage as apiSendMessage,
    getSessions,
    getSessionMessages,
    deleteSession as apiDeleteSession,
    submitToolResult,
    submitToolResultBatch,
    getLlmConfig,
    getSkills,
    uploadAttachment,
    getSessionAttachments,
    deleteAttachment as apiDeleteAttachment,
    getAttachmentDownloadUrl,
    getGlobalConfig,
    BACKEND_URL,
} from "../api/chat-api";
import { getAuthToken } from "../auth/auth-store";
import { getDeviceId } from "../auth/device-id";
import { executeTool, getWorkbookMetadata, initRemoteToolListener } from "../executor/tool-executor";
import { analyzeSheetStructureForLLM, importData, getWorksheetStableId } from "../excel/api";
import { isGibberish } from "../utils/gibberish";
import { registerWorkbook, onConnectedWorkbooksChange } from "../excel/workbook-tracker";

/**
 * Patch 5 — per-parent-assistant-message tool-call batch coordinator state.
 *
 * One BatchState per parent AIMessage that emitted ≥1 tool_call. Tracks
 * sibling tool_call_ids, accumulates their results, and triggers ONE
 * `submitToolResultBatch` POST when complete.
 */
interface BatchState {
  /** Number of sibling tool_calls expected (from SSE siblingCount field). */
  expectedCount: number;
  /** Results pushed by each runTool as it completes. Order doesn't matter
   *  to DeepSeek (matches by tool_call_id), but commit order is preserved. */
  results: Array<{
    toolCallId: string;
    toolName: string;
    result: unknown;
    isError: boolean;
  }>;
  /** All tool_call_ids in this batch — used by abort() to know which slots
   *  need cancellation placeholders when force-firing partial batches. */
  toolCallIds: string[];
  /** tool_call_id → toolName, for placeholder synthesis on partial fire. */
  toolCallNames: Map<string, string>;
  sessionId: string;
  /** Assistant message ID in the UI, used to route SSE events from the
   *  batch's POST response back to the right message bubble. */
  msgId: string;
}

export class ChatController {
  readonly state: Writable<RuntimeState>;
  adapter: any;

  /** One controller per user send / tool round — used to abort SSE fetches */
  private streamAbortController: AbortController | null = null;
  /** User clicked Stop — skip tool execution / submitToolResult */
  private abortRequested = false;
  /** In-flight tool runs (async); streaming UI stays until this hits 0 */
  private pendingToolRuns = 0;
  /** Bumped when user sends or starts a new session switch — stale `switchSession` results must not overwrite UI */
  private sessionSwitchToken = 0;
  /** Resolvers for tools waiting for human confirmation */
  private pendingConfirmation = new Map<string, { resolve: (allowed: boolean) => void, msgId: string }>();
  /** Resolvers for ask_user tool calls waiting for user chip selection */
  private pendingOptionSelect = new Map<string, { resolve: (selected: string) => void; msgId: string }>();
  /** Tracks which assistant messages have already been confirmed by the user for modifier tools */
  private confirmedMessageIds = new Set<string>();
  /** Cached system config — fetched once on first upload */
  private systemConfig: Record<string, string> | null = null;

  /**
   * Patch 5 batch coordinator.
   *
   * For each parent assistant message that emitted N parallel tool_calls,
   * collects all N results in memory and fires ONE `submitToolResultBatch`
   * POST when complete. Single tool_calls (siblingCount=1) fast-path POST
   * immediately on completion.
   *
   * Eliminates the appendMessage race that previously caused silent UI
   * freezes by ensuring the BE only ever sees a single atomic write per
   * parent batch.
   *
   * Keyed on `parentMessageId` from the SSE tool_call event.
   */
  private toolBatches = new Map<string, BatchState>();

  private static readonly MODIFIER_TOOLS = new Set([
     "set_cell_range",
     "write_cells",
     "clear_cell_range",
     "copy_to",
     "modify_sheet_structure",
     "modify_workbook_structure",
     "import_data",
     "insert_batch",
     "eval_officejs"
  ]);

  private hasExcelRuntime(): boolean {
    return typeof Excel !== "undefined" && typeof Excel.run === "function";
  }

  private hasOfficeDocument(): boolean {
    return (
      typeof Office !== "undefined" &&
      !!Office.context?.document &&
      typeof Office.context.document.addHandlerAsync === "function"
    );
  }

  constructor(adapter: any) {
    this.adapter = adapter;

    // Listen for remote tool calls from other workbooks
    initRemoteToolListener();

    this.state = writable({
      messages: [],
      isStreaming: false,
      isLoadingSession: false,
      error: null,
      serverLlmInfo: null,
      sessionStats: {
        inputTokens: 0,
        outputTokens: 0,
        totalCost: 0,
        commandCount: 0
      },
      currentSession: {
        id: crypto.randomUUID(),
        name: "New Chat",
        createdAt: Date.now(),
        updatedAt: Date.now()
      },
      sessions: [],
      nameMap: {},
      uploads: [],
      isUploading: false,
      skills: [],
      pendingSkill: null,
      vfsInvalidatedAt: 0,
      connectedWorkbooks: [],
      currentSelection: null
    });

    // Initial load: fetch sessions and switch to the most recent one
    void this.refreshServerLlmInfo();
    void this.refreshSkills();
    this.refreshSessions().then(() => {
        const sessions = this.snapshot.sessions;
        if (sessions.length > 0) {
            this.switchSession(sessions[0].id).catch(() => {
                // If session load fails, keep new session
            });
        }
    });

    // Initialize Workbook Tracking
    this.initWorkbookTracking();
    this.initSelectionTracking();
  }

  private initWorkbookTracking() {
    if (!this.hasExcelRuntime()) {
      console.warn("[ChatController] Excel runtime unavailable; skipping workbook tracking");
      return;
    }

    Excel.run(async (context) => {
      const workbook = context.workbook;
      workbook.load("name");
      await context.sync();
      
      const unregister = registerWorkbook(workbook.name || "Untitled");
      
      const unsubscribe = onConnectedWorkbooksChange((workbooks) => {
        this.state.update(curr => ({ ...curr, connectedWorkbooks: workbooks }));
      });

      // Cleanup on destroy if needed (though ChatController is usually long-lived)
    }).catch(err => console.error("Failed to init workbook tracking:", err));
  }

  private initSelectionTracking() {
    if (!this.hasOfficeDocument()) {
      console.warn("[ChatController] Office document unavailable; skipping selection tracking");
      return;
    }

    Office.context.document.addHandlerAsync(
      Office.EventType.DocumentSelectionChanged,
      () => this.updateSelectionContext()
    );
    // Initial fetch
    this.updateSelectionContext();
  }

  private async updateSelectionContext() {
    if (!this.hasExcelRuntime()) {
      return;
    }

    try {
      await Excel.run(async (context) => {
        const selection = context.workbook.getSelectedRange();
        const activeSheet = context.workbook.worksheets.getActiveWorksheet();
        selection.load("address");
        activeSheet.load("name,id");
        await context.sync();

        const stableSheetId = await getWorksheetStableId(context, activeSheet);
        const address = selection.address.includes("!") 
          ? selection.address.split("!")[1] 
          : selection.address;

        this.state.update(curr => ({
          ...curr,
          currentSelection: {
            address,
            sheetName: activeSheet.name,
            sheetId: stableSheetId
          }
        }));
      });
    } catch (err) {
      console.error("Failed to update selection context:", err);
    }
  }

  async refreshServerLlmInfo(): Promise<void> {
    try {
      const info = await getLlmConfig();
      this.state.update((curr) => ({ ...curr, serverLlmInfo: info }));
    } catch (err: any) {
      this.state.update((curr) => ({
        ...curr,
        serverLlmInfo: null,
        error: err?.message || curr.error,
      }));
    }
  }

  /**
   * Fetch the built-in skill catalogue from BE. Called on init; failure
   * is non-fatal — the picker simply stays empty if the user can't load
   * skills (e.g. backend unreachable).
   */
  async refreshSkills(): Promise<void> {
    try {
      const skills = await getSkills();
      this.state.update((curr) => ({ ...curr, skills }));
    } catch (err) {
      console.warn("[ChatController] Failed to load skills:", err);
    }
  }

  private saveSessionId(_id: string) {
    // session tracking is now server-side per user
  }

  get snapshot() {
    return get(this.state);
  }

  abort(): void {
    this.abortRequested = true;
    this.streamAbortController?.abort();

    // Patch 5 — force-fire every active batch with cancellation placeholders
    // for tool_calls that haven't completed. ONE atomic POST per batch
    // instead of N independent cancellation POSTs (Patch 4-B1).
    //
    // Why: the appendMessage race that motivated bundling also hits
    // cancellation POSTs. If we fired N separate cancellations they'd race
    // each other AND any in-flight real result. Single batch POST has zero
    // race surface.
    const s = this.snapshot;
    if (s.currentSession) {
      // Resolve pending confirmation prompts so their runTools unblock.
      const last = s.messages[s.messages.length - 1];
      if (last?.role === "assistant") {
        for (const p of last.parts) {
          if (p.type === "toolCall" && p.status === "awaiting_confirmation") {
            const resolver = this.pendingConfirmation.get(p.id);
            if (resolver) {
              resolver(false);
              this.pendingConfirmation.delete(p.id);
            }
          }
        }
      }
      // Resolve pending option-select prompts so their runTools unblock.
      if (last?.role === "assistant") {
        for (const p of last.parts) {
          if (p.type === "toolCall" && p.status === "awaiting_selection") {
            const resolver = this.pendingOptionSelect.get(p.id!);
            if (resolver) {
              resolver.resolve("__cancelled__");
              this.pendingOptionSelect.delete(p.id!);
            }
          }
        }
      }
      // Force-fire every active batch with cancellation placeholders.
      for (const [parentId, batch] of this.toolBatches) {
        this.forceFireBatch(parentId, batch, "user_stopped");
      }
    }

    this.state.update((curr) => ({ ...curr, isStreaming: false }));
  }

  /**
   * Patch 5 — force-fire a partially-complete batch.
   *
   * Used by abort() and any place we need to terminate a batch early.
   * Fills missing slots with `cancelled:true` placeholders so the BE
   * receives the full N results and can resume runGraph (or heal will
   * cover it next turn if even this fails).
   */
  private forceFireBatch(parentId: string, batch: BatchState, reason: string): void {
    if (this.toolBatches.get(parentId) !== batch) return; // already fired
    this.toolBatches.delete(parentId);

    const completedIds = new Set(batch.results.map(r => r.toolCallId));
    for (const tcid of batch.toolCallIds) {
      if (!completedIds.has(tcid)) {
        const tc = batch.toolCallNames.get(tcid) ?? "unknown";
        batch.results.push({
          toolCallId: tcid,
          toolName: tc,
          result: { success: false, cancelled: true, reason },
          isError: true,
        });
      }
    }

    void submitToolResultBatch(
      {
        sessionId: batch.sessionId,
        parentMessageId: parentId,
        results: batch.results,
      },
      (ev) => this.handleEvent(ev, batch.msgId),
      undefined, // NO signal — POST must survive abort
    ).catch((err) =>
      console.warn(`[forceFireBatch ${parentId}] POST failed; BE auto-heal will recover`, err),
    );
  }

  private maybeFinishStreaming(): void {
    if (this.pendingToolRuns > 0) return;
    this.streamAbortController = null;
    this.state.update((curr) => ({ ...curr, isStreaming: false }));
  }

  /** Canned response shown locally when gibberish is detected — not sent to LLM or persisted. */
  private static readonly GIBBERISH_REPLY =
    "Chào bạn, tôi là trợ lí Excel của bạn. Tôi có thể giúp gì cho bạn? Hãy nói rõ câu hỏi nhé! 😊";

  async sendMessage(content: string) {
    const s = this.snapshot;
    if (!content.trim() || s.isStreaming) return;

    // ── Gibberish gate — reply locally, skip API & history ──
    if (isGibberish(content)) {
      const userId = Math.random().toString(36).substring(7);
      const botId = Math.random().toString(36).substring(7);

      const userMsg: ChatMessage = {
        id: userId,
        role: "user",
        parts: [{ type: "text", text: content }],
      };
      const botMsg: ChatMessage = {
        id: botId,
        role: "assistant",
        parts: [{ type: "text", text: ChatController.GIBBERISH_REPLY }],
      };

      // Show in chat but do NOT persist — these ephemeral messages
      // will vanish on page reload / session switch.
      this.state.update((curr) => ({
        ...curr,
        messages: [...curr.messages, userMsg, botMsg],
      }));
      return;
    }

    // Invalidate any in-flight `switchSession` so it cannot overwrite messages / isStreaming after we add the user turn.
    this.sessionSwitchToken++;
    if (s.isLoadingSession) {
      this.state.update((curr) => ({ ...curr, isLoadingSession: false }));
    }

    this.abortRequested = false;
    this.streamAbortController = new AbortController();
    const signal = this.streamAbortController.signal;

    // Ensure session ID is saved
    this.saveSessionId(s.currentSession!.id);

    const userMessage: ChatMessage = {
      id: Math.random().toString(36).substring(7),
      role: "user",
      parts: [{ type: "text", text: content }],
    };

    const assistantMessageId = Math.random().toString(36).substring(7);
    const assistantMessage: ChatMessage = {
      id: assistantMessageId,
      role: "assistant",
      parts: []
    };

    this.state.update(curr => ({
      ...curr,
      messages: [...curr.messages, userMessage, assistantMessage],
      isStreaming: true,
      error: null,
      sessionStats: {
        ...curr.sessionStats,
        commandCount: curr.sessionStats.commandCount + 1
      }
    }));

    try {
      // Quick Win #1: Pre-inject sheet structure so the LLM already knows
      // column layout — eliminates the most common first-step tool call.
      const [workbookContext, activeStructure] = await Promise.all([
        getWorkbookMetadata() as any,
        analyzeSheetStructureForLLM().catch(() => null),
      ]);
      if (activeStructure) {
        workbookContext.activeSheetStructure = activeStructure;
      }
      
      // Inject current selection into workbook context
      if (s.currentSelection) {
        workbookContext.currentSelection = s.currentSelection;
      }

      // Inject connected workbooks into context
      if (s.connectedWorkbooks.length > 1) {
        workbookContext.connectedWorkbooks = s.connectedWorkbooks.map(wb => ({
          id: wb.id,
          name: wb.name
        }));
      }

      // One-shot skill: armed via slash menu or popover, consumed on this send.
      // We only ship the skill IDs — BE resolves them to full instructions via
      // SkillRegistryService so prompt bodies stay server-controlled.
      const oneShotSkill = s.pendingSkill;
      const skillIds = oneShotSkill ? [oneShotSkill.id] : [];
      if (oneShotSkill) {
        this.state.update((curr) => ({ ...curr, pendingSkill: null }));
      }

      await apiSendMessage(
        content,
        workbookContext,
        skillIds,
        [],
        s.currentSession?.id ?? null,
        (event) => {
          this.handleEvent(event, assistantMessageId);
        },
        signal,
      );

      this.maybeFinishStreaming();
      await this.refreshSessions(); // Refresh to update names if changed
      void this.refreshServerLlmInfo();
    } catch (err: any) {
      const aborted = err?.name === "AbortError" || signal.aborted;
      if (!aborted) {
        this.state.update((curr) => ({
          ...curr,
          error: err.message || "Failed to send message",
        }));
      }
      this.maybeFinishStreaming();
    }
  }

  async handleUpload(file: File): Promise<void> {
    const s = this.snapshot;
    if (!s.currentSession || s.isUploading) return;

    // Lazy-fetch system config and validate file size client-side
    if (!this.systemConfig) {
      this.systemConfig = await getGlobalConfig().catch(() => ({}));
    }
    const maxSizeMb = parseInt(this.systemConfig['ATTACHMENT_MAX_SIZE_MB'] ?? '10', 10) || 10;
    const maxSizeBytes = maxSizeMb * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      const fileSizeMb = (file.size / 1024 / 1024).toFixed(1);
      const errMsg: ChatMessage = {
        id: Math.random().toString(36).substring(7),
        role: 'system',
        parts: [{ type: 'text', text: `❌ File **${file.name}** (${fileSizeMb}MB) vượt quá giới hạn ${maxSizeMb}MB. Vui lòng chọn file nhỏ hơn.` }],
      };
      this.state.update((curr) => ({ ...curr, messages: [...curr.messages, errMsg] }));
      return;
    }

    const taskId = crypto.randomUUID();
    const uploadFullStart = performance.now();
    this.state.update((curr) => ({ 
      ...curr, 
      isUploading: true, 
      uploadingFileName: file.name,
      uploadProgress: { stage: 'uploading', pct: 5, message: 'Initiating upload...' }
    }));

    // SSE connection for status
    let statusId: string | null = null;
    const token = getAuthToken();
    const params = new URLSearchParams({ device_id: getDeviceId() });
    if (token) params.set("token", token);
    const es = new EventSource(`${BACKEND_URL}/chat/session/${s.currentSession.id}/upload-status/${taskId}?${params}`);
    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.state.update(curr => ({ ...curr, uploadProgress: data }));
      } catch (err) {
        console.error("Failed to parse upload status:", err);
      }
    };
    es.onerror = (err) => {
      console.warn("SSE connection closed or failed", err);
      es.close();
    };

    try {
      const response = await uploadAttachment(s.currentSession.id, file, taskId);
      const uploadExtractDuration = performance.now() - uploadFullStart;
      console.log(`[Performance] File "${file.name}" uploaded and extracted in ${uploadExtractDuration.toFixed(2)}ms`); 
      
      const systemMsg: ChatMessage = {
        id: Math.random().toString(36).substring(7),
        role: "system",
        parts: [{ type: "text", text: `Đã đính kèm tệp: **${file.name}** và trích xuất dữ liệu thành công.` }],
      };

      this.state.update((curr) => ({
        ...curr,
        messages: [...curr.messages, systemMsg],
        uploads: [
          ...curr.uploads,
          {
            id: response.id,
            name: response.filename,
            size: response.size,
            mimeType: response.mimeType,
            extractedData: response.extractedData,
          },
        ],
      }));

      // REFINED AUTO-IMPORT FLOW
      const isImage = response.mimeType?.startsWith("image/");
      const isPdfOrExcel = response.mimeType?.includes("pdf") || 
                           response.mimeType?.includes("sheet") || 
                           response.mimeType?.includes("excel");

      let jsonData: any[][] | null = null;
      if (response.extractedData) {
        try {
          const rawData = response.extractedData.trim();
          const cleanJsonStr = rawData.replace(/^```json\s*|```$/g, "").trim();
          if (cleanJsonStr.startsWith("[")) {
            jsonData = JSON.parse(cleanJsonStr);
          }
        } catch (e) {
          console.warn("[ChatController] Failed to parse extracted data as JSON:", e);
        }
      }

      const isMassive = jsonData && jsonData.length > 20;

      // Rule: Images always let model handle. PDF/Excel only auto-import if MASSIVE.
      if (!isImage && isPdfOrExcel && isMassive && jsonData) {
        try {
          this.state.update(curr => ({ 
            ...curr, 
            uploadProgress: { stage: 'parsing', pct: 95, message: 'Massive data detected. Importing...' } 
          }));
          
          const baseFileName = response.filename.split(".")[0];
          const importResult = await importData(baseFileName, jsonData);
          
          if (importResult.success) {
            const autoMsg = `Dữ liệu từ tệp **${response.filename}** khá lớn (${jsonData.length} bản ghi), nên tôi đã tự động nhập vào sheet **${importResult.sheetName}**.\n\nBạn có muốn tôi kiểm tra, tính toán hay định dạng gì cho bảng dữ liệu này không?`;
            await this.sendMessage(autoMsg);
          }
        } catch (e) {
          console.error("[ChatController] Auto-import failed:", e);
        }
      } else {
        // Not massive or it's an image - just inform the LLM and let it decide
        const snippet = jsonData ? 
            `Dữ liệu trích xuất (một phần):\n\`\`\`json\n${JSON.stringify(jsonData.slice(0, 5), null, 2)}\n\`\`\`` : 
            (response.extractedData ? `Nội dung trích xuất:\n${response.extractedData.slice(0, 500)}...` : "Không có dữ liệu văn bản trích xuất.");
        
        const attachMsg = `Tôi đã đính kèm tệp **${response.filename}**. ${isImage ? "Đây là một hình ảnh, hãy giúp tôi xử lý nó." : "Dữ liệu tệp này có thể được xử lý trực tiếp."}`;
        // We don't send a message automatically for images/small files unless there's a prompt
        // Actually, normally we just let the system prompt handle it when the user finally sends their message.
        // But for consistency with the "Massive" flow, I'll send a "Ready" message if it's PDF/Excel but not massive.
        if (isPdfOrExcel && !isMassive) {
             console.log("[ChatController] Small PDF/Excel attached, waiting for user command.");
        }
      }
    } catch (err: any) {
      this.state.update((curr) => ({
        ...curr,
        error: err.message || "Failed to upload file",
      }));
    } finally {
      es.close();
      this.state.update((curr) => ({ ...curr, isUploading: false, uploadingFileName: undefined, uploadProgress: undefined }));
    }
  }

  private handleEvent(event: SseEvent, assistantMessageId: string): void {
    if (event.type === "text_chunk") {
      this.updateMessage(assistantMessageId, (parts) => {
        const last = parts[parts.length - 1];
        if (last?.type === "text") {
          return [...parts.slice(0, -1), { ...last, text: (last.text || "") + event.content }];
        }
        return [...parts, { type: "text", text: event.content }];
      });
    } else if (event.type === "thinking_chunk") {
      this.updateMessage(assistantMessageId, (parts) => {
        const last = parts[parts.length - 1];
        if (last?.type === "thinking") {
          return [...parts.slice(0, -1), { ...last, thinking: (last.thinking || "") + event.content }];
        }
        return [...parts, { type: "thinking", thinking: event.content }];
      });
    } else if (event.type === "tool_call") {
      const toolEvent = event as SseToolCallEvent;
      const parentId = (toolEvent as any).parentMessageId as string | undefined;
      const siblingCount = ((toolEvent as any).siblingCount as number | undefined) ?? 1;

      this.updateMessage(assistantMessageId, (parts) => [
        ...parts,
        {
          type: "toolCall",
          id: toolEvent.toolCallId,
          name: toolEvent.toolName,
          args: toolEvent.args,
          status: "running"
        }
      ]);

      // Patch 5 — register tool in the batch coordinator. If this is the
      // first sibling for this parent, create the batch state. Subsequent
      // siblings find the existing batch and add their tool_call_id.
      const sessionId = this.snapshot.currentSession?.id;
      if (parentId && sessionId) {
        let batch = this.toolBatches.get(parentId);
        if (!batch) {
          batch = {
            expectedCount: siblingCount,
            results: [],
            toolCallIds: [],
            toolCallNames: new Map(),
            sessionId,
            msgId: assistantMessageId,
          };
          this.toolBatches.set(parentId, batch);
        }
        batch.toolCallIds.push(toolEvent.toolCallId);
        batch.toolCallNames.set(toolEvent.toolCallId, toolEvent.toolName);
      }

      this.pendingToolRuns++;
      void this.runTool(toolEvent, assistantMessageId, parentId).finally(() => {
        this.pendingToolRuns--;
        this.maybeFinishStreaming();
      });
    } else if (event.type === "usage") {
      const usage = event as SseUsageEvent;
      this.state.update(curr => ({
        ...curr,
        sessionStats: {
          ...curr.sessionStats,
          inputTokens: curr.sessionStats.inputTokens + (usage.inputTokens || 0),
          outputTokens: curr.sessionStats.outputTokens + (usage.outputTokens || 0),
          totalCost: curr.sessionStats.totalCost + (usage.cost || 0)
        }
      }));
    } else if (event.type === "error") {
      this.state.update(curr => ({ ...curr, error: (event as any).message }));
    }
  }

  private updateMessage(id: string, updater: (parts: any[]) => any[]) {
    this.state.update(curr => ({
      ...curr,
      messages: curr.messages.map(m => m.id === id ? { ...m, parts: updater(m.parts) } : m)
    }));
  }

  /**
   * Patch 5 — execute one tool, push result into the parent's batch.
   *
   * No more per-tool POST. When this tool's result is ready (success,
   * error, cancelled, or rejected via confirmation), it lands in the
   * batch coordinator's `results` array. When `results.length` reaches
   * `expectedCount`, the batch fires ONE atomic POST to the BE.
   *
   * If `parentId` is missing (legacy SSE without parentMessageId), we
   * fall back to the old single-POST submitToolResult — keeps backward
   * compatibility with any in-flight UI version.
   */
  private async runTool(event: SseToolCallEvent, msgId: string, parentId: string | undefined) {
    const markStatus = (status: "error" | "complete", resultObj: unknown) => {
      this.updateMessage(msgId, (parts) =>
        parts.map((p) =>
          p.type === "toolCall" && p.id === event.toolCallId
            ? {
                ...p,
                status,
                result: typeof resultObj === "string" ? resultObj : JSON.stringify(resultObj),
              }
            : p,
        ),
      );
    };

    // Push this tool's outcome into the batch, and fire the batch POST
    // if all siblings have reported in. Falls back to legacy single-POST
    // if there's no parentId (older SSE event shape).
    const submitOrBatch = async (
      result: unknown,
      isError: boolean,
    ): Promise<void> => {
      if (!parentId) {
        // Legacy single-POST path — unchanged behavior for old SSE events.
        const sessionId = this.snapshot.currentSession?.id;
        if (!sessionId) return;
        try {
          await submitToolResult(
            {
              sessionId,
              toolCallId: event.toolCallId,
              toolName: event.toolName,
              result,
              isError,
            },
            (ev: SseEvent) => this.handleEvent(ev, msgId),
            this.streamAbortController?.signal,
          );
        } catch (e: any) {
          if (e?.name === "AbortError") return; // abort handled elsewhere
          console.error("[runTool legacy] submitToolResult failed; BE auto-heal will recover", e);
        }
        return;
      }

      const batch = this.toolBatches.get(parentId);
      if (!batch) {
        // Batch was already fired (e.g. by abort()). No-op — BE has the
        // batch already; this tool's result was filled with a placeholder.
        return;
      }

      batch.results.push({
        toolCallId: event.toolCallId,
        toolName: event.toolName,
        result,
        isError,
      });

      if (batch.results.length < batch.expectedCount) {
        // Wait for siblings. They'll fire the batch when complete.
        return;
      }

      // All siblings reported. Fire ONE atomic POST.
      this.toolBatches.delete(parentId);
      try {
        await submitToolResultBatch(
          {
            sessionId: batch.sessionId,
            parentMessageId: parentId,
            results: batch.results,
          },
          (ev: SseEvent) => this.handleEvent(ev, msgId),
          this.streamAbortController?.signal,
        );
      } catch (e: any) {
        if (e?.name === "AbortError" || this.streamAbortController?.signal?.aborted) {
          // Abort path — BE auto-heal (Patch 4-D) will inject placeholders next turn.
          // Also try one fire-and-forget without signal as a best-effort.
          void submitToolResultBatch(
            { sessionId: batch.sessionId, parentMessageId: parentId, results: batch.results },
            () => {},
            undefined,
          ).catch(() => {});
          return;
        }
        console.error(`[runTool batch ${parentId}] POST failed; BE auto-heal will recover`, e);
      }
    };

    // ─── Pre-execute abort check ───
    if (this.abortRequested) {
      markStatus("error", { cancelled: true, message: "Stopped by user" });
      await submitOrBatch(
        { success: false, cancelled: true, reason: "user_stopped_before_execute" },
        true,
      );
      return;
    }

    // ─── ask_user interaction intercept ───
    if (event.toolName === "ask_user") {
      this.updateMessage(msgId, (parts) =>
        parts.map((p) =>
          p.type === "toolCall" && p.id === event.toolCallId
            ? { ...p, status: "awaiting_selection" }
            : p,
        ),
      );

      const selected = await new Promise<string>((resolve) => {
        this.pendingOptionSelect.set(event.toolCallId, { resolve, msgId });
      });
      this.pendingOptionSelect.delete(event.toolCallId);

      if (selected === "__cancelled__") {
        markStatus("error", { cancelled: true, message: "Stopped by user" });
        await submitOrBatch({ success: false, cancelled: true, reason: "user_stopped" }, true);
        return;
      }

      markStatus("complete", { selected });
      await submitOrBatch({ selected }, false);
      return;
    }

    // ─── Modifier-tool confirmation ───
    if (ChatController.MODIFIER_TOOLS.has(event.toolName)) {
      if (!this.confirmedMessageIds.has(msgId)) {
        this.updateMessage(msgId, (parts) =>
          parts.map((p) => (p.type === "toolCall" && p.id === event.toolCallId ? { ...p, status: "awaiting_confirmation" } : p)),
        );
        const allowed = await new Promise<boolean>((resolve) => {
          this.pendingConfirmation.set(event.toolCallId, { resolve, msgId });
        });
        this.pendingConfirmation.delete(event.toolCallId);
        if (!allowed) {
          markStatus("error", "Action rejected by user.");
          await submitOrBatch(
            { success: false, error: "USER_REJECTED", message: "User refused to execute this action." },
            true,
          );
          return;
        }
      }
      
      this.updateMessage(msgId, (parts) =>
        parts.map((p) => (p.type === "toolCall" && p.id === event.toolCallId ? { ...p, status: "running" } : p)),
      );
    }

    // ─── Execute Office.js tool ───
    const forceOverwrite = ChatController.MODIFIER_TOOLS.has(event.toolName);
    let result: { result: unknown; isError: boolean };
    try {
      // Race against a 45s timeout so a hung Excel.run() never blocks the batch coordinator
      const toolTimeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error(`tool_timeout:${event.toolName}:45000ms`)), 45_000),
      );
      result = await Promise.race([executeTool(event, forceOverwrite), toolTimeout]);
    } catch (e: any) {
      // Patch 4 Layer A + C — synthesize an error result so the batch can
      // still close. executeTool already has internal try/catch, but
      // executeRemoteTool (timeout, parse-error) could escape.
      console.error(`[runTool] executeTool threw for ${event.toolName}`, e);
      result = {
        result: { success: false, error: "execution_threw", message: String(e?.message ?? e) },
        isError: true,
      };
    }

    // ─── Post-execute abort check ───
    if (this.abortRequested) {
      markStatus("error", { cancelled: true, message: "Stopped by user" });
      await submitOrBatch(
        { success: false, cancelled: true, reason: "user_stopped_after_execute" },
        true,
      );
      return;
    }

    markStatus(result.isError ? "error" : "complete", result.result);
    await submitOrBatch(result.result, result.isError);
  }

  /**
   * Arm a skill for one-shot use — it will attach to the next sendMessage
   * and then auto-clear. Pass null to disarm.
   */
  setPendingSkill(skill: import("./types").SkillMeta | null): void {
    this.state.update((curr) => ({ ...curr, pendingSkill: skill }));
  }

  async newSession() {
    this.sessionSwitchToken++;
    const newId = crypto.randomUUID();
    this.saveSessionId(newId);
    this.confirmedMessageIds.clear();
    this.state.update((curr) => ({
      ...curr,
      messages: [],
      isLoadingSession: false,
      currentSession: {
        id: newId,
        name: "New Chat",
        createdAt: Date.now(),
        updatedAt: Date.now(),
      },
      sessionStats: {
        inputTokens: 0,
        outputTokens: 0,
        totalCost: 0,
        commandCount: 0
      },
      uploads: [],
      isUploading: false,
      uploadingFileName: undefined,
      uploadProgress: undefined,
      isStreaming: false,
      error: null
    }));
    await this.refreshSessions();
  }

  async switchSession(sessionId: string) {
    const token = ++this.sessionSwitchToken;
    this.saveSessionId(sessionId);
    this.confirmedMessageIds.clear();
    this.state.update((curr) => ({ ...curr, isLoadingSession: true }));
    try {
      const [messages, attachments] = await Promise.all([
        getSessionMessages(sessionId),
        getSessionAttachments(sessionId)
      ]);

      if (token !== this.sessionSwitchToken) {
        this.state.update((curr) => ({ ...curr, isLoadingSession: false }));
        return;
      }

      // Map attachments to UploadedFile type
      const uploads = attachments.map((att: any, idx: number) => ({
        id: att.id || `missing-id-${idx}`,
        name: att.filename,
        size: att.size,
        extractedData: att.snippet 
      }));

      // Map persisted API messages (user / assistant+toolCalls / tool results) → UI parts.
      const uiMessages: ChatMessage[] = [];
      let currentAssistantMessage: ChatMessage | null = null;

      type ApiToolCall = { id?: string; name?: string; arguments?: Record<string, unknown>; args?: Record<string, unknown> };
      type ApiMsg = {
        role: string;
        content?: string;
        toolCalls?: ApiToolCall[];
        toolCallId?: string;
        toolName?: string;
        isError?: boolean;
      };

      for (const m of messages as ApiMsg[]) {
          if (m.role === "user") {
              const text = typeof m.content === "string" ? m.content : "";
              uiMessages.push({
                  id: Math.random().toString(36).substring(7),
                  role: "user",
                  parts: [{ type: "text", text }]
              });
              currentAssistantMessage = null;
          } else if (m.role === "assistant") {
              currentAssistantMessage = {
                  id: Math.random().toString(36).substring(7),
                  role: "assistant",
                  parts: []
              };
              uiMessages.push(currentAssistantMessage);

              const text = typeof m.content === "string" ? m.content : "";
              if (text.trim()) {
                  currentAssistantMessage.parts.push({ type: "text", text });
              }

              const calls = m.toolCalls ?? [];
              for (const tc of calls) {
                  const args = tc.arguments ?? tc.args ?? {};
                  const id = tc.id ?? `tc-${Math.random().toString(36).substring(7)}`;
                  const name = (tc.name && String(tc.name).trim()) || "tool";
                  currentAssistantMessage.parts.push({
                      type: "toolCall",
                      id,
                      name,
                      args,
                      status: "running",
                  });
              }
          } else if (m.role === "tool") {
              if (!currentAssistantMessage) {
                  currentAssistantMessage = {
                      id: Math.random().toString(36).substring(7),
                      role: "assistant",
                      parts: [],
                  };
                  uiMessages.push(currentAssistantMessage);
              }

              const resultStr =
                  typeof m.content === "string" ? m.content : JSON.stringify(m.content ?? "");
              const tid = m.toolCallId;
              const tname = (m.toolName && String(m.toolName).trim()) || "tool";

              if (tid) {
                  const idx = currentAssistantMessage.parts.findIndex(
                      (p) => p.type === "toolCall" && p.id === tid,
                  );
                  if (idx >= 0) {
                      const prev = currentAssistantMessage.parts[idx];
                      currentAssistantMessage.parts[idx] = {
                          ...prev,
                          name: (prev as { name?: string }).name || tname,
                          status: m.isError ? "error" : "complete",
                          result: resultStr,
                      };
                  } else {
                      currentAssistantMessage.parts.push({
                          type: "toolCall",
                          id: tid,
                          name: tname,
                          args: {},
                          status: m.isError ? "error" : "complete",
                          result: resultStr,
                      });
                  }
              } else {
                  currentAssistantMessage.parts.push({
                      type: "toolCall",
                      id: `tool-${Math.random().toString(36).substring(7)}`,
                      name: tname,
                      args: {},
                      status: m.isError ? "error" : "complete",
                      result: resultStr,
                  });
              }
          } else if (m.role === "system") {
              uiMessages.push({
                  id: Math.random().toString(36).substring(7),
                  role: "system",
                  parts: [{ type: "text", text: m.content }]
              });
          }
      }

      // Any tool row still "running" had no matching tool message — show as complete with result if any.
      for (const msg of uiMessages) {
          if (msg.role !== "assistant") continue;
          for (const p of msg.parts) {
              if (p.type === "toolCall" && p.status === "running") {
                  p.status = "complete";
              }
          }
      }

      if (token !== this.sessionSwitchToken) {
        this.state.update((curr) => ({ ...curr, isLoadingSession: false }));
        return;
      }

      // Don't overwrite an active conversation — sendMessage may have already set
      // isStreaming:true and added messages before this switchSession (called from
      // constructor init) completed its network round-trip.
      if (this.snapshot.isStreaming) {
        this.state.update((curr) => ({ ...curr, isLoadingSession: false }));
        return;
      }

      this.state.update(curr => {
        const session = curr.sessions.find(s => s.id === sessionId);
        return {
          ...curr,
          messages: uiMessages,
          uploads: uploads as any[],
          currentSession: session || {
              id: sessionId,
              name: "Restored Chat",
              createdAt: Date.now(),
              updatedAt: Date.now()
          },
          sessionStats: {
            inputTokens: session?.inputTokens ?? 0,
            outputTokens: session?.outputTokens ?? 0,
            totalCost: session?.totalCost ?? 0,
            commandCount: session?.commandCount ?? 0
          },
          isLoadingSession: false
        };
      });
    } catch (err: any) {
      if (token !== this.sessionSwitchToken) {
        this.state.update((curr) => ({ ...curr, isLoadingSession: false }));
        return;
      }
      this.state.update(curr => ({
        ...curr,
        isLoadingSession: false,
        error: err?.message || "Failed to switch session",
      }));
      throw err;
    }
  }

  async deleteCurrentSession() {
    const s = this.snapshot;
    if (s.currentSession) {
      await apiDeleteSession(s.currentSession.id);
      await this.newSession();
    }
  }

  async refreshSessions() {
    try {
      const sessions = await getSessions();
      this.state.update(curr => ({ ...curr, sessions }));
    } catch (err: any) {
      this.state.update(curr => ({ ...curr, error: err?.message || curr.error }));
    }
  }

  clearMessages() {
    this.state.update(curr => ({ ...curr, messages: [] }));
  }

  getDownloadUrl(id: string): string {
    return getAttachmentDownloadUrl(id);
  }

  async deleteAttachment(id: string) {
    try {
      await apiDeleteAttachment(id);
      this.state.update(curr => ({
        ...curr,
        uploads: curr.uploads.filter(u => u.id !== id)
      }));
    } catch (err) {
      console.error("Failed to delete attachment:", err);
    }
  }

  selectOption(toolCallId: string, selected: string) {
    const pending = this.pendingOptionSelect.get(toolCallId);
    if (pending) {
      this.pendingOptionSelect.delete(toolCallId);
      pending.resolve(selected);
    } else {
      console.warn(`[ChatController] No pending selectOption resolver for id: ${toolCallId}`);
    }
  }

  confirmToolCall(id: string, allowed: boolean) {
    console.log(`[ChatController] 🛡️ confirmToolCall received: id=${id}, allowed=${allowed}`);
    const pending = this.pendingConfirmation.get(id);
    if (pending) {
        console.log(`[ChatController] ✅ Resolver found for ${id}. Resolving with ${allowed}`);
        const { msgId } = pending;
        if (allowed) {
            this.confirmedMessageIds.add(msgId);
        }
        
        // Resolve all pending for this msgId
        for (const [tId, p] of this.pendingConfirmation.entries()) {
            if (p.msgId === msgId) {
                p.resolve(allowed);
                this.pendingConfirmation.delete(tId);
            }
        }
    } else {
        console.warn(`[ChatController] ❌ No pending resolver found for tool call id: ${id}. Current pending:`, Array.from(this.pendingConfirmation.keys()));
    }
  }
}
